import { Injectable } from "@angular/core";
import { AlertController, LoadingController, ModalController } from "@ionic/angular";
import { TranslocoService } from "@ngneat/transloco";
import { ChecklistProgressStateStore } from "source/app/features/checklists/state/checklist-progress-state.store";
import { ChecklistStateStore } from "source/app/features/checklists/state/checklist-state.store";
import { ActionsStore } from "../state/actions.store";
import { EnvironmentStore } from "../state/environment.store";
import { LocationStateStore } from "../state/location-state.store";
import { UiQuery } from "../state/ui.query";
import { UiService } from "../state/ui.service";
import { UIStore } from "../state/ui.store";
import { StorageService } from "./storage.service";
import { FileTranslationsStore } from "../state/file-translations.store";
import { FileUploadsStore } from "../state/file-uploads.store";
import { RideOpsUiService } from "./rideops-ui.service";
import { FactoryResetConfirmComponent } from "source/app/modals/factory-reset-confirm/factory-reset-confirm.component";

@Injectable({
	providedIn: "root",
})
export class FactoryResetService {
	constructor(
		private translocoService: TranslocoService,
		private alertController: AlertController,
		private modalController: ModalController,
		private rideOpsUi: RideOpsUiService,
		private uiQuery: UiQuery,
		private uiService: UiService,
		private uiStore: UIStore,
		private actionsStore: ActionsStore,
		private environmentStore: EnvironmentStore,
		private locationStateStore: LocationStateStore,
		private checklistStore: ChecklistStateStore,
		private checklistProgressStore: ChecklistProgressStateStore,
		private fileTranslationsStore: FileTranslationsStore,
		private fileUploadsStore: FileUploadsStore,
		private storageService: StorageService,
		private loadingController: LoadingController,
	) {}

	public factoryReset = async (): Promise<void> => {
		const confirmed = this.rideOpsUi.isV2Active() ? await this.confirmV2() : await this.confirmV1();

		if (!confirmed) {
			return;
		}

		await this.nuke();
	};

	private confirmV2 = async (): Promise<boolean> => {
		const modal = await this.modalController.create({
			component: FactoryResetConfirmComponent,
			cssClass: "factory-reset-modal-v2",
			showBackdrop: true,
		});

		await modal.present();
		const result = await modal.onDidDismiss<boolean>();
		return result.data === true;
	};

	private confirmV1 = async (): Promise<boolean> => {
		const alert = await this.alertController.create({
			header: this.translocoService.translate("FACTORY_RESET__TITLE"),
			message: this.translocoService.translate("FACTORY_RESET__DESCRIPTION"),
			buttons: [
				{
					text: this.translocoService.translate("FACTORY_RESET__CANCEL"),
					role: "cancel",
				},
				{
					text: this.translocoService.translate("FACTORY_RESET__OK"),
					role: "ok",
				},
			],
		});

		await alert.present();
		const result = await alert.onDidDismiss();
		return result.role === "ok";
	};

	private nuke = async () => {
		// we need to do a blocking screen to allow all debounced writes to settle while ressetting stores
		const loading = await this.loadingController.create({
			message: this.translocoService.translate("LOADING_MESSAGE__PLEASE_WAIT"),
			duration: 3000,
		});

		loading.present();

		const preserveV2 = this.rideOpsUi.isV2Active();
		const rideOpsUiV2OptIn = this.uiQuery.getRideOpsUiV2OptIn();
		const rideOpsUiV2FlagAvailable = this.uiQuery.getRideOpsUiV2FlagAvailable();
		const dispatchUndoOptIn = this.uiQuery.getDispatchUndoOptIn();
		const rememberRiderCount = this.uiQuery.getRememberRiderCount();

		this.uiStore.reset();
		this.actionsStore.reset();
		this.locationStateStore.reset();
		this.environmentStore.reset();
		this.checklistStore.reset();
		this.checklistProgressStore.reset();
		this.fileTranslationsStore.reset();
		this.fileUploadsStore.reset();
		await this.storageService.reset();

		if (preserveV2) {
			this.uiService.setRideOpsUiV2OptIn(rideOpsUiV2OptIn);
			this.uiService.setRideOpsUiV2FlagAvailable(rideOpsUiV2FlagAvailable);
			this.uiService.setDispatchUndoOptIn(dispatchUndoOptIn);
			this.uiService.setRememberRiderCount(rememberRiderCount);
			localStorage.setItem("ui@store", JSON.stringify(this.uiStore.store.getValue()));
		}

		await loading.onDidDismiss();
		document.location.reload();
	};
}
